import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { onAuthStateChanged, signOut, type User } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { saveTheme, THEME_NAMES, type ThemeName } from "../lib/theme";

// Mirrors the native AuthService phases exactly.
export type Phase = "loading" | "signedOut" | "needsProfile" | "ready";

interface AuthState {
  phase: Phase;
  user: User | null;
  uid: string | null;
  /** Re-check whether the user's profile doc exists. */
  refresh: () => Promise<void>;
  /** Call right after a confirmed profile write to advance into the app. */
  profileSaved: () => void;
  signOutUser: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used within <AuthProvider>");
  return v;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [user, setUser] = useState<User | null>(null);

  async function resolve(u: User | null) {
    if (!u) {
      setUser(null);
      setPhase("signedOut");
      return;
    }
    setUser(u);
    try {
      const snap = await getDoc(doc(db, "users", u.uid));
      // Restore the account's saved theme on sign-in (localStorage is per-app
      // on iOS — the installed PWA can't see what Safari saved, so the account
      // is the source of truth).
      const d = snap.data();
      const t = d?.themeName as ThemeName | undefined;
      if (t && THEME_NAMES.includes(t)) {
        saveTheme(t, typeof d?.themeIntensity === "number" ? d.themeIntensity : 1);
      }
      setPhase(snap.exists() ? "ready" : "needsProfile");
    } catch {
      // A read failure must never be read as "no account" — that would strand a
      // signed-in user or bounce them to profile setup. Keep any resolved phase.
      setPhase((p) => (p === "ready" || p === "needsProfile" ? p : "loading"));
    }
  }

  useEffect(() => onAuthStateChanged(auth, (u) => void resolve(u)), []);

  const value: AuthState = {
    phase,
    user,
    uid: user?.uid ?? null,
    refresh: () => resolve(auth.currentUser),
    profileSaved: () => setPhase("ready"),
    signOutUser: () => signOut(auth),
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
